<template>
  <v-container>
    <v-snackbar v-model="snackbar" class="elevation-24" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" @click="snackbar = false" />
      </template>
    </v-snackbar>

    <!-- Event Header Card -->
    <v-card class="pa-5 mb-4" v-if="event" elevation="2">
      <v-img
        :src="'/uploads/' + event.image"
        height="260px"
        width="100%"
        class="align-end mb-4"
        gradient="to bottom, rgba(0,0,0,.2), rgba(0,0,0,.7)"
        cover
        v-if="event.image"
      >
        <v-card-title class="text-white text-h5 pa-4">{{ event.name }}</v-card-title>
      </v-img>
      <v-card-title v-else>{{ event.name }}</v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <p><strong>Description:</strong> {{ event.description }}</p>
            <p><strong>Date:</strong> {{ formatDate(event.date) }}</p>
            <p v-if="event.location"><strong>Location:</strong> {{ event.location }}</p>
          </v-col>
          <v-col cols="12" sm="6">
            <p><strong>Status:</strong>
              <v-chip :color="event.status ? 'success' : 'grey'" size="small" class="ml-1">
                {{ event.status ? 'Active' : 'Inactive' }}
              </v-chip>
            </p>
            <p><strong>Max Invitations:</strong> {{ event.max_invitations }}</p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Attendee Stats Row -->
    <v-row class="mb-3" v-if="attendees.length > 0">
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="blue" class="pa-3 text-center">
          <div class="text-h5 font-weight-bold">{{ stats.total }}</div>
          <div class="text-caption">Total</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="green" class="pa-3 text-center">
          <div class="text-h5 font-weight-bold">{{ stats.checkedIn }}</div>
          <div class="text-caption">Checked In</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="indigo" class="pa-3 text-center">
          <div class="text-h5 font-weight-bold">{{ stats.males }}</div>
          <div class="text-caption">Male</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card variant="tonal" color="pink" class="pa-3 text-center">
          <div class="text-h5 font-weight-bold">{{ stats.females }}</div>
          <div class="text-caption">Female</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Check-in rate bar -->
    <v-card variant="outlined" class="pa-3 mb-4" v-if="attendees.length > 0">
      <div class="d-flex justify-space-between mb-1">
        <span class="text-body-2">Check-in Rate</span>
        <span class="text-body-2 font-weight-bold">{{ stats.rate }}%</span>
      </div>
      <v-progress-linear
        :model-value="stats.rate"
        :color="stats.rate >= 75 ? 'success' : stats.rate >= 40 ? 'warning' : 'error'"
        height="10"
        rounded
        bg-color="grey-lighten-3"
      />
    </v-card>

    <!-- Attendees Table -->
    <v-card class="pa-4 my-4" elevation="1">
      <div class="d-flex align-center mb-3">
        <span class="text-subtitle-1 font-weight-medium flex-grow-1">Attendees</span>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search attendees"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="max-width: 260px;"
        />
      </div>
      <v-data-table
        :headers="headers"
        :items="attendees"
        item-key="id"
        :items-per-page="10"
        :search="search"
        density="compact"
      >
        <template v-slot:[`item.checked_in`]="{ item }">
          <v-icon :color="item.checked_in == 1 ? 'success' : 'grey-lighten-1'" size="20">
            {{ item.checked_in == 1 ? 'mdi-check-circle' : 'mdi-circle-outline' }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Action Buttons -->
    <v-row class="mb-3">
      <v-col cols="12" sm="6">
        <v-btn prepend-icon="mdi-export" block color="success" @click="exportCSV(event.id)">
          Export Attendees
        </v-btn>
      </v-col>
      <v-col cols="12" sm="6">
        <v-btn
          prepend-icon="mdi-download"
          block
          color="info"
          :loading="isGenerating"
          @click="sendInvitations(event.id)"
        >
          Download Invitations
        </v-btn>
      </v-col>
    </v-row>

    <v-progress-linear
      v-if="isGenerating"
      :model-value="progress"
      color="blue"
      height="6"
      rounded
      class="mb-4"
    />

    <!-- Upload Attendees -->
    <v-card variant="outlined" class="pa-4 mb-4">
      <div class="text-subtitle-1 font-weight-medium mb-3">Upload Attendees from Excel</div>
      <v-row align="center">
        <v-col cols="12" sm="8">
          <v-file-input
            v-model="uploadedFile"
            label="Select Excel file (.xlsx / .xls)"
            accept=".xlsx, .xls"
            prepend-icon="mdi-upload"
            variant="outlined"
            density="compact"
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-btn
            color="success"
            block
            @click="uploadAttendees"
            :disabled="!uploadedFile"
          >
            Upload
          </v-btn>
        </v-col>
      </v-row>
      <p v-if="message" class="text-caption mt-2 text-grey">{{ message }}</p>
    </v-card>

    <!-- Back Button -->
    <v-btn prepend-icon="mdi-arrow-left" @click="goBack" color="grey-darken-3" variant="outlined" block>
      Back to Events
    </v-btn>
  </v-container>
</template>

<script>
import axios from "axios";
const XLSX = require("xlsx");
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default {
  props: ["id"],
  data() {
    return {
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      event: null,
      uploadedFile: null,
      attendees: [],
      message: "",
      search: "",
      headers: [
        { title: "Name",       value: "name",       sortable: true },
        { title: "Email",      value: "email" },
        { title: "Gender",     value: "gender" },
        { title: "Age",        value: "age" },
        { title: "Checked In", value: "checked_in" },
      ],
      isGenerating: false,
      progress: 0,
    };
  },
  computed: {
    stats() {
      const total     = this.attendees.length;
      const checkedIn = this.attendees.filter(a => a.checked_in == 1).length;
      const males     = this.attendees.filter(a => a.gender === "Male").length;
      const females   = this.attendees.filter(a => a.gender === "Female").length;
      return {
        total,
        checkedIn,
        males,
        females,
        rate: total > 0 ? Math.round((checkedIn / total) * 100) : 0,
      };
    },
  },
  mounted() {
    this.fetchEvent();
    this.fetchAttendees(this.id);
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    },
    async fetchEvent() {
      try {
        const response = await axios.get(`/api/events/${this.id}`);
        this.event = response.data;
      } catch (error) {
        console.error("Error fetching event details:", error);
      }
    },
    fetchAttendees(eventId) {
      axios.get(`/api/events/${eventId}/attendees/export`).then((response) => {
        this.attendees = response.data.data;
      });
    },
    async sendInvitations() {
      const zip = new JSZip();
      this.isGenerating = true;
      this.progress = 0;

      const batchSize = 5;
      for (let i = 0; i < this.attendees.length; i += batchSize) {
        const batch = this.attendees.slice(i, i + batchSize);
        for (const invite of batch) {
          await this.generateSinglePDF(invite, zip);
        }
        this.progress = Math.min(100, ((i + batch.length) / this.attendees.length) * 100);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      this.isGenerating = false;
      this.progress = 0;
      saveAs(zipBlob, "all_invitations.zip");
    },
    async generateSinglePDF(invite, zip) {
      const doc = new jsPDF();
      const pageWidth  = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const qrBoxWidth  = 70;
      const qrBoxHeight = 80;
      const qrBoxX = (pageWidth - qrBoxWidth) / 2;
      const qrBoxY = 120;
      const qrSize = 50;
      const qrX = qrBoxX + (qrBoxWidth - qrSize) / 2;
      const qrY = qrBoxY + 10;

      const qrCodeDataUrl = await QRCode.toDataURL(`${invite.event_id}*${invite.name}*${invite.email}`);

      doc.saveGraphicsState();
      doc.setTextColor(230, 230, 230);
      doc.setFontSize(50);
      doc.setFont("helvetica", "bold");
      doc.text("Sunrise Events", pageWidth / 2, pageHeight / 2, { align: "center", angle: 45 });
      doc.restoreGraphicsState();

      doc.setDrawColor("#000000");
      doc.setLineDashPattern([4, 2], 0);
      doc.setLineWidth(0.8);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
      doc.setLineDashPattern([], 0);

      doc.setFillColor("#000000");
      doc.rect(10, 10, pageWidth - 20, 30, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFontSize(18);

      const imageUrl  = `/uploads/${invite.event_image}`;
      const imageBlob = await fetch(imageUrl).then((res) => res.blob());
      const imageReader = await this.blobToBase64(imageBlob);
      doc.addImage(imageReader, "PNG", 15, 12, qrSize / 2, qrSize / 2);

      doc.text(`${invite.event_name} — You're Invited!`, pageWidth / 2, 28, { align: "center" });

      doc.setFontSize(14);
      doc.setTextColor("#000000");
      doc.text(`Name: ${invite.name}`,      20, 55);
      doc.text(`Email: ${invite.email}`,    20, 65);
      doc.text(`Event ID: ${invite.event_id}`, 20, 75);

      doc.setFontSize(12);
      doc.setTextColor("#333333");
      doc.text(invite.description || "", 20, 90, { maxWidth: pageWidth - 40 });

      doc.setDrawColor("#888888");
      doc.setLineWidth(0.5);
      doc.rect(qrBoxX, qrBoxY, qrBoxWidth, qrBoxHeight);
      doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

      doc.setFontSize(11);
      doc.setTextColor("#333333");
      doc.text("Present this code at the entrance for fast check-in.", pageWidth / 2, qrBoxY + qrBoxHeight + 30, { align: "center" });

      doc.setFillColor("#000000");
      doc.rect(10, pageHeight - 30, pageWidth - 20, 20, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFontSize(10);
      doc.text("Sunrise Events · © 2025", pageWidth / 2, pageHeight - 17, { align: "center" });

      const pdfBlob = doc.output("blob");
      zip.file(`${invite.event_name}-${invite.name}.pdf`, pdfBlob);
    },
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    goBack() {
      this.$router.push("/");
    },
    exportCSV(eventId) {
      const rows = this.attendees.map((att) => ({
        name:       att.name,
        email:      att.email,
        gender:     att.gender,
        age:        att.age,
        checked_in: att.checked_in ? "Yes" : "No",
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook  = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `attendees_event_${eventId}.xlsx`);
    },
    async uploadAttendees() {
      try {
        const data      = await this.uploadedFile.arrayBuffer();
        const workbook  = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const attendees = XLSX.utils.sheet_to_json(worksheet);

        if (!attendees.every((a) => a.name && a.email)) {
          this.snackbarText  = 'Each row must have "name" and "email" fields.';
          this.snackbar      = true;
          this.snackbarColor = "red";
          return;
        }

        await axios
          .post(`/api/events/attendees/upload/${this.id}`, { attendees })
          .then((resp) => {
            this.snackbarText  = resp.data.message;
            this.snackbar      = true;
            this.snackbarColor = "success";
          })
          .catch((err) => {
            this.snackbarText  = String(err);
            this.snackbar      = true;
            this.snackbarColor = "red";
          });

        this.message      = "Upload successful!";
        this.uploadedFile = null;
        this.fetchAttendees(this.id);
      } catch (error) {
        console.error(error);
        this.message = "Upload failed: " + error;
      }
    },
  },
};
</script>

<style scoped>
a {
  color: #1976d2;
  text-decoration: none;
}
a:hover {
  text-decoration: underline;
}
</style>
